import * as React from 'react'
import { cn } from '@merels/ui'
import { MateCard } from '../../mateCard/MateCard/MateCard'
import type { Mate } from '../MateList/MateList'

export interface TeamProps {
  mates: Mate[]
  ourMateId?: string
  /** Present only when the caller wants clicking our own mate's tile to open its settings. */
  onOurMateClick?: () => void
  /** Removes a locally owned bot from the room. */
  onDismissBot?: (mateId: string) => void
  /** Owner only: make a mate a host or remove its host status. */
  onToggleHost?: (mateId: string) => void
  /** Omni mode: act as the clicked Omni mate. */
  onActAs?: (mateId: string) => void

  header?: React.ReactNode
  headerWrapperClassName?: string

  /**
   * Styling inputs for the team container.
   *
   * The container is responsible for the "team level" styling:
   * - right border color/width only (no top/bottom/left)
   *
   * Precedence rules (documented so `MateList` stays simple):
   * - **Explicit `borderColor` wins** (normal player teams): solid right border, 4px.
   * - Else **`fallbackBorderColor`**: solid right border, 2px (subtle "current player" hint in test/local-test).
   * - Else: default `border-r border-basic` (neutral right border).
   */
  borderColor?: string
  fallbackBorderColor?: string

  /** When true: apply pointer affordances for click-to-select teams. */
  isInteractive?: boolean

  /**
   * Draws a thin divider in `mates` right before this index (e.g. separating Omni
   * mates from real mates - see mateListVM.ts's omniObserverCount). Omitted/undefined
   * draws no divider.
   */
  separatorIndex?: number

  className?: string
  onClick?: () => void
  'data-testid'?: string
}

export const Team = React.forwardRef<HTMLDivElement, TeamProps>(
  (
    {
      mates,
      ourMateId,
      onOurMateClick,
      onDismissBot,
      onToggleHost,
      onActAs,
      header,
      headerWrapperClassName,
      borderColor,
      fallbackBorderColor,
      isInteractive = false,
      separatorIndex,
      className,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const style: React.CSSProperties = {
      ...(borderColor
        ? {
            borderRightColor: borderColor,
            borderRightWidth: '4px',
            borderRightStyle: 'solid',
          }
        : fallbackBorderColor
          ? {
              borderRightColor: fallbackBorderColor,
              borderRightWidth: '2px',
              borderRightStyle: 'solid',
            }
          : {}),
    }

    const shouldUseDefaultBorder = !borderColor && !fallbackBorderColor

    return (
      <div
        ref={ref}
        className={cn(
          'border-r pr-2 pb-1 transition-colors',
          className,
          shouldUseDefaultBorder && 'border-basic',
          isInteractive && 'cursor-pointer hover:border-primary/50',
        )}
        style={style}
        onClick={onClick}
        {...rest}
      >
        {header && <div className={cn('mb-1', headerWrapperClassName)}>{header}</div>}

        <div className="space-y-2">
          {mates.map((mate, index) => (
            <React.Fragment key={mate.id}>
              {index === separatorIndex && <div className="border-t border-basic/40" />}
              <MateCard
                mate={mate}
                isOurMate={mate.id === ourMateId}
                botDisplayName={mate.botDisplayName}
                noBackground={mate.source === 'shared-screen'}
                onClick={onOurMateClick}
                onActAs={mate.canActAs && onActAs ? () => onActAs(mate.id) : undefined}
                onDismiss={mate.isBot && mate.canDismiss ? () => onDismissBot?.(mate.id) : undefined}
                onToggleHost={mate.canToggleHost && onToggleHost ? () => onToggleHost(mate.id) : undefined}
              />
            </React.Fragment>
          ))}
        </div>
      </div>
    )
  },
)
Team.displayName = 'Team'
